// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React from "react";
import {
  Box,
  Divider,
  List,
  ListItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SystemStyleObject,
  forwardRef,
  useMergeRefs,
} from "@chakra-ui/react";
import {
  UseComboboxGetItemPropsOptions,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  useCombobox,
} from "downshift";
import { Modifier } from "@popperjs/core";

import { TextField, TextFieldProps } from "../text-field";

export interface AutocompleteSuggestionListProps {
  value?: string;
  isOpen?: boolean;
  suggestions?: unknown[];
  highlightedIndex?: number;
  getItemProps?: UseComboboxGetItemPropsOptions<unknown>;
}

export const AutocompleteSuggestionList = forwardRef<
  AutocompleteSuggestionListProps,
  "ul"
>(
  (
    { value, isOpen, suggestions, highlightedIndex, getItemProps, sx, ...rest },
    ref
  ) => {
    return (
      <List
        sx={{
          boxShadow: "rgb(0 0 0 / 32%) 0px 4px 16px",
          maxHeight: "420px",
          overflowY: "scroll",
          ...sx,
        }}
        ref={ref}
        {...rest}
      />
    );
  }
);

export interface AutocompleteSuggestionListItemProps {
  index?: number;
  highlightedIndex?: number;
  isLast?: boolean;
}

export const AutocompleteSuggestionListItem = forwardRef<
  AutocompleteSuggestionListItemProps,
  "li"
>(({ children, sx, index, highlightedIndex, isLast, ...rest }, ref) => (
  <>
    <ListItem
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flex: 1,
        padding: "small",
        backgroundColor:
          highlightedIndex === index ? "rgba(0, 0, 0, 0.06)" : "white",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </ListItem>
    {isLast ? null : <Divider />}
  </>
));

export const matchWidthModifier: Modifier<"matchWidth", any> = {
  name: "matchWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.minWidth = "auto";
    state.styles.popper.maxWidth = `${state.rects.reference.width}px`;
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => () => {
    const reference = state.elements.reference as HTMLElement;
    state.elements.popper.style.width = `${reference.offsetWidth}px`;
  },
};

function stateReducer(
  state: UseComboboxState<unknown>,
  actionAndChanges: UseComboboxStateChangeOptions<unknown>
): Partial<UseComboboxState<unknown>> {
  const { type, changes } = actionAndChanges;
  switch (type) {
    case useCombobox.stateChangeTypes.InputChange:
      return {
        // return normal changes.
        ...changes,
        isOpen: changes.inputValue ? changes.inputValue.length !== 0 : false,
      };
    default:
      return changes;
  }
}

export interface AutocompleteInputProps extends TextFieldProps {
  suggestions?: unknown[];
  SuggestionListItem?: React.ReactNode;
  SuggestionList?: React.ReactNode;
  onSelectSuggestion?: (changes: UseComboboxStateChange<unknown>) => void;
  onChange?: (value: string) => void;
  suggestionToString?: (suggestion: unknown) => string;
  listContainerSx?: SystemStyleObject;
  popoverContentSx?: SystemStyleObject;
  suggestionsOffset?: number;
  matchWidth?: boolean;
  keepSuggestionsOpen?: boolean;
}

export const AutocompleteInput = forwardRef<AutocompleteInputProps, "input">(
  (
    {
      onChange,
      value = "",
      suggestions = [],
      onSelectSuggestion,
      suggestionToString,
      SuggestionListItem = AutocompleteSuggestionListItem,
      SuggestionList = AutocompleteSuggestionList,
      listContainerSx,
      popoverContentSx,
      suggestionsOffset = 0,
      matchWidth = true,
      keepSuggestionsOpen = false,
      ...rest
    },
    ref
  ) => {
    const {
      isOpen: boxIsOpen,
      getMenuProps,
      highlightedIndex,
      getItemProps,
      getInputProps,
      getComboboxProps,
    } = useCombobox({
      items: suggestions,
      itemToString: suggestionToString
        ? suggestionToString
        : (suggestion) => suggestion,
      stateReducer: stateReducer,
      initialIsOpen: false,
      defaultIsOpen: false,
      onInputValueChange: ({ inputValue }) => {
        if (onChange) {
          onChange(inputValue || "");
        }
      },
      onSelectedItemChange: onSelectSuggestion,
    });

    const { ref: downshiftInputRef, ...inputProps } = getInputProps();
    const refs = useMergeRefs(ref, downshiftInputRef);

    const isOpen = keepSuggestionsOpen || boxIsOpen;

    return (
      <Popover
        isOpen={isOpen}
        autoFocus={false}
        modifiers={[{ ...matchWidthModifier, enabled: matchWidth }]}
        gutter={suggestionsOffset}
      >
        <PopoverTrigger>
          <Box {...getComboboxProps()} width="100%">
            <TextField
              {...inputProps}
              value={value}
              ref={refs}
              {...rest}
              borderBottomRadius={isOpen && suggestions.length ? 0 : undefined}
            />
          </Box>
        </PopoverTrigger>
        <PopoverContent
          {...getMenuProps()}
          sx={{
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            width: "100%",
            border: "none",
            ...popoverContentSx,
          }}
        >
          {(keepSuggestionsOpen || suggestions.length > 0) && (
            <SuggestionList
              value={value}
              suggestions={suggestions}
              isOpen={isOpen}
              highlightedIndex={highlightedIndex}
              getItemProps={getItemProps}
              sx={{
                boxShadow: "rgb(0 0 0 / 32%) 0px 4px 16px",
                maxHeight: "420px",
                overflowY: "auto",
                overflowX: "hidden",
                ...listContainerSx,
              }}
            >
              {isOpen &&
                suggestions.map((item, index) => (
                  <SuggestionListItem
                    key={index}
                    {...getItemProps({ item, index })}
                    index={index}
                    isLast={suggestions.length - 1 === index}
                    highlightedIndex={highlightedIndex}
                  >
                    {suggestionToString ? suggestionToString(item) : item}
                  </SuggestionListItem>
                ))}
            </SuggestionList>
          )}
        </PopoverContent>
      </Popover>
    );
  }
);
